import type { FormikProps } from 'formik';
import { Text } from '@rsos/base-components';
import { ErrorWrapper } from '@rsos/base-components/form/ErrorsWrapper';
import { Select } from '@rsos/components/capstone/base';
import { Label } from '../../../../../AirAmbulanceForm/AirAmbulanceForm/AirAmbulanceFormStandalone.styles';
import type {
  TowProFormValues,
  TowProSelectOption,
} from '../towProFormConstants';
import styles from './FormField.module.css';
import type { TowProStringField } from './fieldTypes';
import { useSelectStyles } from './useSelectStyles';

export interface SelectFieldProps {
  formik: FormikProps<TowProFormValues>;
  name: TowProStringField;
  label: string;
  options: TowProSelectOption[];
  placeholder: string;
  isLoading?: boolean;
  /** Lets an optional field be emptied again once a value is picked. */
  isClearable?: boolean;
  /** Lets the user type to narrow the options. */
  isSearchable?: boolean;
  /** Decides whether an option matches the typed input when searchable. */
  filterOption?: (option: TowProSelectOption, input: string) => boolean;
  /**
   * Offers the typed text as the value when no option matches it. Requires
   * `isSearchable`.
   */
  allowTypedValue?: boolean;
  /**
   * Replaces the default single-field update. Use it for a field that has to
   * change together with another one, so that validation sees both at once.
   */
  onChange?: (value: string) => void;
}

/** Fallback matching when no `filterOption` is given: the label contains the input. */
function matchesLabel(option: TowProSelectOption, input: string): boolean {
  return option.label.toLowerCase().includes(input.trim().toLowerCase());
}

function SelectField({
  formik,
  name,
  label,
  options,
  placeholder,
  isLoading = false,
  isClearable = false,
  isSearchable = false,
  filterOption,
  allowTypedValue = false,
  onChange,
}: SelectFieldProps): JSX.Element {
  const currentValue = formik.values[name];
  const selectedOption =
    options.find(option => option.value === currentValue) ??
    (allowTypedValue && currentValue
      ? { value: currentValue, label: currentValue }
      : null);

  const matchesInput = filterOption ?? matchesLabel;
  const typedValueProps = allowTypedValue && {
    creatable: true,
    isValidNewOption: (input: string) =>
      input.trim().length > 0 &&
      !options.some(option => matchesInput(option, input)),
    // react-select passes the `formatCreateLabel` text as the label; only the
    // value is stored, so the field shows the plain typed text once picked.
    getNewOptionData: (input: string, label: string): TowProSelectOption => ({
      value: input.trim(),
      label,
    }),
    formatCreateLabel: (input: string) => `Use "${input.trim()}"`,
  };
  const error =
    formik.touched[name] && formik.errors[name]
      ? String(formik.errors[name])
      : undefined;

  const selectStyles = useSelectStyles(error);

  return (
    <div className={styles.field}>
      <Label>
        <Text size="small" color="primaryText">
          {label}
        </Text>
      </Label>
      <Select
        name={name}
        classNamePrefix={name}
        options={options}
        placeholder={placeholder}
        isClearable={isClearable}
        isSearchable={isSearchable}
        filterOption={
          filterOption &&
          ((option: { data: TowProSelectOption }, input: string) =>
            filterOption(option.data, input))
        }
        {...typedValueProps}
        isLoading={isLoading}
        onChange={(option: TowProSelectOption | null) => {
          const value = option?.value ?? '';
          if (onChange) {
            onChange(value);
          } else {
            formik.setFieldValue(name, value, true);
          }
          // Mark touched without validating so the stale empty value never
          // renders a transient Required error.
          formik.setFieldTouched(name, true, false);
        }}
        // Touch only. react-select fires blur right after a selection, and
        // Formik would validate it against the values from before that
        // selection, overwriting the errors the change just computed.
        onBlur={() => formik.setFieldTouched(name, true, false)}
        value={selectedOption}
        styles={selectStyles}
      />
      {error && <ErrorWrapper error={error} />}
    </div>
  );
}

export default SelectField;
